import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { CensoOSEsitoRichiesta, CensoOSImpostazioni, Probe, ProbeCensoOS } from '../api/types'
import { Card, ErrorBanner, StatCard, formatDateTime } from './common'
import { DataTable, type Column } from './DataTable'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * CensoOS on the probes' appliances (censoos_fleet_service): the version the
 * mirror serves, the maintenance window, the brake, and for each appliance the
 * installed version, the restart Debian asks, the request and its outcome.
 * «Update CensoOS» and «Restart» go to the selected ones, now or in the window;
 * with the automatic mode on, the Control Plane asks them by itself in the
 * window, one probe per group at a time.
 */
export function CensoOSAppliance({ probes, onChanged }: { probes: Probe[]; onChanged: () => void }) {
  const riepilogo = useAsync(() => api.censoos(), [])
  const [scelte, setScelte] = useState<Set<string>>(new Set())
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const [scartate, setScartate] = useState<CensoOSEsitoRichiesta['skipped']>([])
  const [impostazioni, setImpostazioni] = useState(false)
  const righe = probes.filter((p) => p.censoos)
  const r = riepilogo.data

  if (righe.length === 0) return null

  const ricarica = () => {
    riepilogo.reload()
    onChanged()
  }
  const esegui = async (azione: () => Promise<unknown>, fatto: string) => {
    setLavoro(true)
    setErrore(null)
    setEsito(null)
    setScartate([])
    try {
      const risposta = await azione()
      setEsito(fatto)
      if (risposta && typeof risposta === 'object' && 'skipped' in risposta) {
        setScartate((risposta as CensoOSEsitoRichiesta).skipped)
      }
      ricarica()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }
  const ids = [...scelte]
  const chiedi = (azione: 'update' | 'reboot', quando: 'now' | 'window') => {
    const testo = azione === 'update'
      ? (quando === 'now'
        ? t('Aggiornare CensoOS adesso su {n} appliance? Ognuna si riavvia: la cattura si ferma per qualche minuto.', { n: ids.length })
        : t('Aggiornare CensoOS su {n} appliance nella prossima finestra di manutenzione? Ognuna si riavvierà.', { n: ids.length }))
      : (quando === 'now'
        ? t('Riavviare adesso {n} appliance? La cattura si ferma per qualche minuto.', { n: ids.length })
        : t('Riavviare {n} appliance nella prossima finestra di manutenzione?', { n: ids.length }))
    if (!window.confirm(testo)) return
    void esegui(() => api.censoosRequest({ probe_ids: ids, action: azione, when: quando }),
      quando === 'now' ? t('Richiesta inviata: le appliance la ricevono al prossimo battito.') : t('Richiesta programmata nella finestra di manutenzione.'))
  }
  const annulla = (p: Probe) => {
    if (!window.confirm(t('Annullare la richiesta per {nome}?', { nome: p.name }))) return
    void esegui(() => api.censoosCancelRequest(p.id), t('Richiesta annullata.'))
  }

  const colonne: Column<Probe>[] = [
    { key: 'name', header: t('Sonda'), value: (p) => p.name, render: (p) => <Link to={`/fleet/${p.id}`}>{p.name}</Link> },
    { key: 'installed', header: t('CensoOS'), mono: true, value: (p) => p.censoos?.installed ?? '' },
    {
      key: 'outdated', header: t('Aggiornamento'),
      value: (p) => (p.censoos?.outdated ? t('da aggiornare') : t('aggiornata')),
      render: (p) => p.censoos && <Aggiornamento c={p.censoos} />,
    },
    {
      key: 'reboot', header: t('Riavvio richiesto'),
      value: (p) => (p.censoos?.reboot_required ? t('sì') : t('no')),
      render: (p) => p.censoos?.reboot_required
        ? <span title={(p.censoos.reboot_packages ?? []).join(', ')} style={{ color: 'var(--warn)' }}>{t('sì')}</span>
        : <span className="muted">{t('no')}</span>,
    },
    {
      key: 'request', header: t('Richiesta / esito'),
      value: (p) => (p.censoos?.request ? faseRichiesta(p.censoos.request.phase) : p.censoos?.last ? faseEsito(p.censoos.last.phase) : ''),
      render: (p) => p.censoos && <StatoRichiesta c={p.censoos} onAnnulla={() => annulla(p)} lavoro={lavoro} />,
    },
    {
      key: 'auto', header: t('Automatico'),
      value: (p) => (p.censoos?.auto ? t('acceso') : t('spento')),
      render: (p) => (p.censoos?.auto ? t('acceso') : <span className="muted">{t('spento')}</span>),
    },
  ]

  return (
    <Card title={t('CensoOS delle appliance')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Il sistema delle appliance fisiche: si aggiorna da qui (solo pacchetti firmati, con ritorno alla versione precedente se la sonda non torna), subito o nella finestra di manutenzione. Con l’automatico acceso lo fa il Control Plane da solo nella finestra, una sonda per gruppo alla volta, e si ferma dopo due fallimenti sulla stessa versione.')}
      </p>
      <ErrorBanner error={errore ?? riepilogo.error} />
      {esito && <div className="banner banner--ok">{esito}</div>}
      {scartate.length > 0 && (
        <div className="banner banner--warn">
          {t('Non inviata a:')} {scartate.map((s) => `${s.name ?? s.id} (${motivo(s.reason)})`).join(', ')}
        </div>
      )}
      {r?.brake && (
        <div className="banner banner--warn">
          {t('Aggiornamenti automatici fermi: CensoOS {v} è fallito due volte in una settimana. Guarda l’esito sulle appliance; da qui si può ancora chiedere a mano.', { v: r.available ?? '' })}
        </div>
      )}
      {r && (
        <div className="stats">
          <StatCard label={t('Versione disponibile')} value={r.available ?? '—'} />
          <StatCard label={t('Appliance')} value={r.appliances} />
          <StatCard label={t('Da aggiornare')} value={r.outdated} tone={r.outdated ? 'warn' : 'ok'} />
          <StatCard label={t('Riavvio richiesto')} value={r.reboot_required} tone={r.reboot_required ? 'warn' : 'ok'} />
          <StatCard label={t('Richieste in corso')} value={r.pending} />
        </div>
      )}
      {r && (
        <p className="field__hint">
          {r.available ? null : <>{t('Nessun aggiornamento di CensoOS su questa piattaforma.')} </>}
          {r.window.start && (r.window.open
            ? t('Finestra di manutenzione aperta fino alle {fine}.', { fine: formatDateTime(r.window.end) })
            : t('Prossima finestra di manutenzione: {inizio} – {fine}.', { inizio: formatDateTime(r.window.start), fine: formatDateTime(r.window.end) }))}
          {' '}
          <button type="button" className="link" onClick={() => setImpostazioni((v) => !v)}>
            {impostazioni ? t('chiudi') : t('Finestra e automatico')}
          </button>
          {r.not_remote > 0 && <><br />{t('{n} appliance con CensoOS 0.1.0: la prima volta si aggiornano dal loro menu (Updates → Update CensoOS), poi da qui.', { n: r.not_remote })}</>}
        </p>
      )}
      {impostazioni && r && <Impostazioni valori={r.settings} onSalvato={() => { setImpostazioni(false); ricarica() }} />}

      {scelte.size > 0 && (
        <div className="pianificazioni-massiva">
          <strong>{t('{n} appliance selezionate', { n: scelte.size })}</strong>
          <button type="button" disabled={lavoro} onClick={() => chiedi('update', 'window')}>{t('Aggiorna nella finestra')}</button>
          <button type="button" className="secondary" disabled={lavoro} onClick={() => chiedi('update', 'now')}>{t('Aggiorna adesso')}</button>
          <button type="button" className="secondary" disabled={lavoro} onClick={() => chiedi('reboot', 'window')}>{t('Riavvia nella finestra')}</button>
          <button type="button" className="secondary" disabled={lavoro} onClick={() => chiedi('reboot', 'now')}>{t('Riavvia adesso')}</button>
          <button type="button" className="secondary" disabled={lavoro}
                  onClick={() => void esegui(() => api.censoosAuto({ probe_ids: ids, enabled: true }), t('Automatico acceso.'))}>{t('Automatico: accendi')}</button>
          <button type="button" className="secondary" disabled={lavoro}
                  onClick={() => void esegui(() => api.censoosAuto({ probe_ids: ids, enabled: false }), t('Automatico spento.'))}>{t('Automatico: spegni')}</button>
          <button type="button" className="link" onClick={() => setScelte(new Set())}>{t('Annulla la selezione')}</button>
        </div>
      )}
      <DataTable rows={righe} columns={colonne} rowKey={(p) => p.id} conteggio={{ nome: t('appliance') }}
                 selezione={{ chiavi: scelte, onCambia: setScelte, id: (p) => p.id }} />
    </Card>
  )
}

function Aggiornamento({ c }: { c: ProbeCensoOS }) {
  if (!c.available) return <span className="muted">—</span>
  if (!c.outdated) return <span className="muted">{t('aggiornata')}</span>
  return (
    <span style={{ color: 'var(--warn)' }}>
      {t('{v} disponibile', { v: c.available })}
      {!c.remote && <div className="field__hint" style={{ margin: 0 }}>{t('dal menu dell’appliance, la prima volta')}</div>}
    </span>
  )
}

/** The request under way (with «annulla» while not taken), or the last outcome. */
export function StatoRichiesta({ c, onAnnulla, lavoro }: { c: ProbeCensoOS; onAnnulla?: () => void; lavoro?: boolean }) {
  const q = c.request
  if (q) {
    return (
      <span>
        {q.action === 'update' ? t('Aggiornamento') : t('Riavvio')} · {faseRichiesta(q.phase)}
        {q.phase === 'waiting' && q.not_before && <> · {t('dalle {ora}', { ora: formatDateTime(q.not_before) })}</>}
        {q.auto && <> · {t('automatico')}</>}
        {q.message && <div className="field__hint" style={{ margin: 0 }}>{q.message}</div>}
        {q.phase === 'waiting' && onAnnulla && (
          <div><button type="button" className="link" disabled={lavoro} onClick={onAnnulla}>{t('annulla')}</button></div>
        )}
      </span>
    )
  }
  const e = c.last
  if (!e) return <span className="muted">—</span>
  const colore = e.phase === 'done' ? undefined : 'var(--warn)'
  return (
    <span style={{ color: colore }} title={e.message}>
      {e.action === 'update' ? t('Aggiornamento') : t('Riavvio')} · {faseEsito(e.phase)} · {formatDateTime(e.closed_at)}
      {e.message && <div className="field__hint" style={{ margin: 0 }}>{e.message}</div>}
    </span>
  )
}

function faseRichiesta(fase: string): string {
  switch (fase) {
    case 'waiting': return t('in attesa')
    case 'running': return t('in corso')
    case 'rebooting': return t('riavvio in corso')
    case 'verifying': return t('verifica della sonda')
    default: return fase
  }
}

function faseEsito(fase: string): string {
  switch (fase) {
    case 'done': return t('riuscito')
    case 'failed': return t('fallito')
    case 'rolled_back': return t('tornato alla versione precedente')
    case 'expired': return t('senza esito')
    default: return fase
  }
}

function motivo(codice: string): string {
  switch (codice) {
    case 'not_censoos': return t('non è un’appliance CensoOS')
    case 'not_remote': return t('CensoOS 0.1.0: aggiornala una volta dal suo menu')
    case 'disabled': return t('sonda disattivata o revocata')
    case 'pending': return t('c’è già una richiesta')
    case 'no_update': return t('nessun aggiornamento sulla piattaforma')
    case 'up_to_date': return t('già aggiornata')
    case 'not_found': return t('sonda non trovata')
    default: return codice
  }
}

const GIORNI = [
  { n: 0, l: 'lun' }, { n: 1, l: 'mar' }, { n: 2, l: 'mer' }, { n: 3, l: 'gio' },
  { n: 4, l: 'ven' }, { n: 5, l: 'sab' }, { n: 6, l: 'dom' },
]

function Impostazioni({ valori, onSalvato }: { valori: CensoOSImpostazioni; onSalvato: () => void }) {
  const [v, setV] = useState<CensoOSImpostazioni>(valori)
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const salva = async () => {
    setLavoro(true)
    setErrore(null)
    try {
      await api.censoosSettings(v)
      onSalvato()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }
  const etichetta: Record<string, string> = {
    lun: t('lun'), mar: t('mar'), mer: t('mer'), gio: t('gio'), ven: t('ven'), sab: t('sab'), dom: t('dom'),
  }
  return (
    <form className="form-dialog" onSubmit={(e) => { e.preventDefault(); void salva() }}>
      <h4 className="form__sezione">{t('Finestra di manutenzione')}</h4>
      <ErrorBanner error={errore} />
      <div className="form-grid">
        <div className="field span-4">
          <span className="field__label">{t('Giorni')}</span>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {GIORNI.map((g) => (
              <label key={g.n} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <input type="checkbox" checked={v.days.includes(g.n)}
                       onChange={(e) => setV({ ...v, days: e.target.checked ? [...v.days, g.n].sort() : v.days.filter((d) => d !== g.n) })} />
                {etichetta[g.l]}
              </label>
            ))}
          </div>
        </div>
        <label className="field span-1"><span className="field__label">{t('Dalle')}</span>
          <input type="time" value={v.start} onChange={(e) => setV({ ...v, start: e.target.value })} required /></label>
        <label className="field span-1"><span className="field__label">{t('Alle')}</span>
          <input type="time" value={v.end} onChange={(e) => setV({ ...v, end: e.target.value })} required /></label>
        <label className="field span-2"><span className="field__label">{t('Fuso orario')}</span>
          <input type="text" value={v.timezone} onChange={(e) => setV({ ...v, timezone: e.target.value })} placeholder={'Europe/Rome'} required /></label>
        <label className="field span-2"><span className="field__label">{t('Appliance insieme, al più')}</span>
          <input type="number" min={1} max={20} value={v.max_parallel} onChange={(e) => setV({ ...v, max_parallel: Number(e.target.value) || 1 })} /></label>
        <label className="field span-2" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={v.reboot_auto} onChange={(e) => setV({ ...v, reboot_auto: e.target.checked })} />
          <span>{t('Con l’automatico, riavvia anche quando Debian lo chiede (kernel nuovo)')}</span>
        </label>
      </div>
      <p className="field__hint">{t('Una finestra può attraversare la mezzanotte (es. 22:00–02:00). Una richiesta che la sonda non prende nella finestra passa alla successiva.')}</p>
      <div className="form-dialog__azioni">
        <button type="submit" disabled={lavoro || v.days.length === 0}>{lavoro ? t('Salvo…') : t('Salva')}</button>
      </div>
    </form>
  )
}

/** CensoOS on one probe's appliance, for its page: the same actions. */
export function CensoOSSonda({ probe, onChanged }: { probe: Probe; onChanged: () => void }) {
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const c = probe.censoos
  if (!c) return null
  const esegui = async (azione: () => Promise<unknown>, fatto: string) => {
    setLavoro(true)
    setErrore(null)
    setEsito(null)
    try {
      const r = await azione()
      const scartata = r && typeof r === 'object' && 'skipped' in r ? (r as CensoOSEsitoRichiesta).skipped[0] : undefined
      setEsito(scartata ? `${t('Non inviata:')} ${motivo(scartata.reason)}` : fatto)
      onChanged()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }
  const chiedi = (azione: 'update' | 'reboot', quando: 'now' | 'window') => {
    const testo = azione === 'update'
      ? t('Aggiornare CensoOS su {nome}{quando}? L’appliance si riavvia.', { nome: probe.name, quando: quando === 'now' ? ` ${t('adesso')}` : ` ${t('nella prossima finestra di manutenzione')}` })
      : t('Riavviare {nome}{quando}? La cattura si ferma per qualche minuto.', { nome: probe.name, quando: quando === 'now' ? ` ${t('adesso')}` : ` ${t('nella prossima finestra di manutenzione')}` })
    if (!window.confirm(testo)) return
    void esegui(() => api.censoosRequest({ probe_ids: [probe.id], action: azione, when: quando }), t('Richiesta inviata.'))
  }
  return (
    <Card title={t('CensoOS')}>
      <ErrorBanner error={errore} />
      {esito && <div className="banner banner--ok">{esito}</div>}
      <dl className="kv">
        <dt>{t('Installata')}</dt><dd className="mono">{c.installed}</dd>
        <dt>{t('Disponibile')}</dt><dd><Aggiornamento c={c} /></dd>
        <dt>{t('Riavvio richiesto')}</dt>
        <dd>{c.reboot_required ? <>{t('sì')}{c.reboot_packages.length > 0 && <span className="muted"> · {c.reboot_packages.join(', ')}</span>}</> : t('no')}</dd>
        <dt>{t('Richiesta / esito')}</dt>
        <dd><StatoRichiesta c={c} lavoro={lavoro} onAnnulla={() => {
          if (!window.confirm(t('Annullare la richiesta per {nome}?', { nome: probe.name }))) return
          void esegui(() => api.censoosCancelRequest(probe.id), t('Richiesta annullata.'))
        }} /></dd>
        <dt>{t('Automatico nella finestra')}</dt><dd>{c.auto ? t('acceso') : t('spento')}</dd>
      </dl>
      {!c.remote ? (
        <p className="field__hint">{t('Questa appliance ha CensoOS 0.1.0: la prima volta si aggiorna dal suo menu (Updates → Update CensoOS); da lì in poi da qui.')}</p>
      ) : (
        <div className="rowactions" style={{ textAlign: 'left' }}>
          <button type="button" disabled={lavoro || !c.outdated || Boolean(c.request)} onClick={() => chiedi('update', 'window')}>{t('Aggiorna nella finestra')}</button>
          <button type="button" className="secondary" disabled={lavoro || !c.outdated || Boolean(c.request)} onClick={() => chiedi('update', 'now')}>{t('Aggiorna adesso')}</button>
          <button type="button" className="secondary" disabled={lavoro || Boolean(c.request)} onClick={() => chiedi('reboot', 'now')}>{t('Riavvia adesso')}</button>
          <button type="button" className="secondary" disabled={lavoro}
                  onClick={() => void esegui(() => api.censoosAuto({ probe_ids: [probe.id], enabled: !c.auto }), c.auto ? t('Automatico spento.') : t('Automatico acceso.'))}>
            {c.auto ? t('Automatico: spegni') : t('Automatico: accendi')}
          </button>
        </div>
      )}
    </Card>
  )
}
